import { useCallback, useEffect, useRef, useState } from 'react'
import { apiClient } from '../services/api-client'
import { NearbyVenue, SpotlightVenue } from '../types/liveMap'
import { LatLng } from './useVenuesNearby'

/**
 * Fetches the spotlight ticker from GET user/venues/spotlighted — the same
 * server-ranked list the mobile ticker uses, sized/ranked by the admin's
 * Spotlight settings (FeatureFlags editor). Refetches as the map center moves.
 */

interface UseSpotlightVenuesResult {
  spotlights: SpotlightVenue[]
  loading: boolean
}

function coerceToList(data: unknown): SpotlightVenue[] {
  if (Array.isArray(data)) return data as SpotlightVenue[]
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>
    for (const key of ['venues', 'items', 'data', 'results']) {
      if (Array.isArray(obj[key])) return obj[key] as SpotlightVenue[]
    }
  }
  return []
}

/** Adapts a spotlight card to the venue shape used by the map + detail sheet. */
export function spotlightToNearby(s: SpotlightVenue): NearbyVenue {
  return {
    publicId: s.publicId,
    name: s.name,
    address: null,
    logoUrl: s.logoUrl ?? null,
    latitude: s.latitude ?? 0,
    longitude: s.longitude ?? 0,
    totalCheckins: s.totalCheckins ?? null,
    eventRsvpCount: s.eventRsvpCount ?? null,
    visitPlanCount: s.visitPlanCount ?? null,
    breadcrumbCount: s.breadcrumbCount ?? s.breadcrumbsCount ?? null,
    recentCheckins: s.recentCheckins ?? null,
    peeks: s.peeks ?? s.interestedUsers ?? null,
    interestedUsers: s.interestedUsers ?? null,
  }
}

export function useSpotlightVenues(center: LatLng | null): UseSpotlightVenuesResult {
  const [spotlights, setSpotlights] = useState<SpotlightVenue[]>([])
  const [loading, setLoading] = useState(false)
  const requestIdRef = useRef(0)

  const fetchSpotlights = useCallback(async (c: LatLng) => {
    const requestId = ++requestIdRef.current
    setLoading(true)
    try {
      const response = await apiClient.get('/user/venues/spotlighted', {
        params: { latitude: String(c.lat), longitude: String(c.lng) },
      })
      if (requestId !== requestIdRef.current) return
      setSpotlights(coerceToList(response.data).filter(v => v.publicId && v.isHidden !== true && v.isClosed !== true))
    } catch (err) {
      if (requestId !== requestIdRef.current) return
      console.error('Failed to load spotlight venues:', err)
    } finally {
      if (requestId === requestIdRef.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (center) fetchSpotlights(center)
  }, [center, fetchSpotlights])

  return { spotlights, loading }
}
